/**
 * draft.js — surviving a reload, and taking an edit back.
 *
 * Both halves of "I set everything up and hit refresh" live here: the draft
 * (persistence across page loads) and the history (undo within one session).
 *
 * ── WHY THIS SHAPE, measured rather than chosen ────────────────────────────
 *
 * Three ways to persist a project were measured against two real 74 MB takes:
 *
 *   A  state only (user re-picks the files)   works everywhere
 *   B  state + the video bytes in IndexedDB   Chromium 3 ms · Firefox 0.3–5 s
 *   C  File System Access handles             `showOpenFilePicker` absent on WebKit
 *
 * B restores with no user action at all, and the bytes genuinely survive: after
 * a hard reload the blobs came back byte-identical (77 315 551 B), the MP4
 * header was intact (`ftypisom`) and the video DECODED — 45 s, 1920 px, real
 * pixels. Decoding is the proof; a Blob of the right size that will not decode
 * is not a restored project.
 *
 * But B is not universal. On WebKit, IndexedDB refused a Blob of **eleven
 * bytes** while accepting a 1 MB ArrayBuffer in the same store — so it is not a
 * quota and not a size, it is Blob support. C is Chromium-only.
 *
 * Hence: **A is the floor and B is an upgrade**, chosen at runtime by writing
 * one 11-byte Blob. That probe predicted the 148 MB outcome exactly on all
 * three engines, which is why it is trusted instead of a version sniff.
 *
 * ── The history cap is in BYTES, not steps ─────────────────────────────────
 * A snapshot costs 0.008 ms and ~1 KB for an ordinary project, and 1.5 ms and
 * 235 KB once the mask has been hand-painted — a 200× spread. A step cap would
 * mean "60 undos" in one project and 13 MB of heap in the other, so the budget
 * is bytes and the depth follows from it.
 *
 * Exposes window.CloneDraft.
 */
(function () {
'use strict';
if (window.CloneDraft) return;

var DB_NAME = 'timbrica_clone_effect_draft';
var DB_VERSION = 1;
var STORE = 'draft';
var KEY = 'current';
var MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
var SAVE_DEBOUNCE_MS = 900;

/* ─────────────────────────────── plumbing ─────────────────────────────── */

var _db = null;
function open() {
    if (_db) return Promise.resolve(_db);
    return new Promise(function (res, rej) {
        if (typeof indexedDB === 'undefined') { rej(new Error('no-idb')); return; }
        var r = indexedDB.open(DB_NAME, DB_VERSION);
        r.onupgradeneeded = function () {
            if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE);
        };
        r.onsuccess = function () { _db = r.result; res(_db); };
        r.onerror = function () { rej(r.error || new Error('open')); };
    });
}

function put(key, value) {
    return open().then(function (db) {
        return new Promise(function (res, rej) {
            var tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).put(value, key);
            tx.oncomplete = res;
            tx.onerror = function () { rej(tx.error || new Error('tx')); };
            tx.onabort = function () { rej(tx.error || new Error('abort')); };
        });
    });
}

function get(key) {
    return open().then(function (db) {
        return new Promise(function (res, rej) {
            var tx = db.transaction(STORE, 'readonly');
            var q = tx.objectStore(STORE).get(key);
            q.onsuccess = function () { res(q.result); };
            q.onerror = function () { rej(q.error); };
        });
    });
}

function del(key) {
    return open().then(function (db) {
        return new Promise(function (res) {
            var tx = db.transaction(STORE, 'readwrite');
            tx.objectStore(STORE).delete(key);
            tx.oncomplete = tx.onerror = tx.onabort = function () { res(); };
        });
    }).catch(function () {});
}

/* ── can this engine hold a Blob at all? ──
   Eleven bytes. Measured to predict the 148 MB case exactly on Chromium,
   Firefox and WebKit, which is why the answer is taken from a write rather
   than from a user-agent string. Cached for the page's life. */
var _blobProbe = null;
function canStoreBlobs() {
    if (_blobProbe) return _blobProbe;
    _blobProbe = put('__probe', new Blob(['probe-blob']))
        .then(function () { return del('__probe').then(function () { return true; }); })
        .catch(function () { return false; });
    return _blobProbe;
}

/* ───────────────────────── the serialisable project ─────────────────────────
   Deliberately NOT the live object: Files are handled separately, and the
   caches hanging off a clip (the clean plate, decoded audio, speech spans) are
   derived data that must be recomputed rather than restored — a stale plate
   would silently mask the wrong footage. */

function serialise(P) {
    return {
        v: 1,
        W: P.W, H: P.H, fps: P.fps, preset: P.preset, bg: P.bg, quality: P.quality,
        frame: P.frame ? { mode: P.frame.mode, win: P.frame.win } : null,
        clips: P.clips.map(function (c) {
            return {
                file: fileTag(c.file),
                name: c.name, duration: c.duration, vw: c.vw, vh: c.vh,
                trimIn: c.trimIn, trimOut: c.trimOut, start: c.start, hold: c.hold,
                dx: c.dx, dy: c.dy, scale: c.scale, fit: c.fit, flipH: c.flipH,
                exposure: c.exposure, tint: c.tint, offset: c.offset,
                volume: c.volume, muted: c.muted, audioOffset: c.audioOffset,
                hasAudio: c.hasAudio, visible: c.visible,
                mask: c.mask
            };
        }),
        music: P.music ? {
            file: fileTag(P.music.file),
            name: P.music.name, duration: P.music.duration,
            volume: P.music.volume, fadeIn: P.music.fadeIn, fadeOut: P.music.fadeOut,
            loop: P.music.loop, duck: P.music.duck, duckDepth: P.music.duckDepth,
            start: P.music.start, trimIn: P.music.trimIn, trimOut: P.music.trimOut
        } : null
    };
}

/** Identity of a file the user might re-pick: name + size + mtime. */
function fileTag(f) {
    if (!f) return null;
    return { name: f.name || '', size: f.size || 0, lastModified: f.lastModified || 0 };
}

function sameFile(tag, f) {
    return !!(tag && f && tag.name === f.name && tag.size === f.size
        && (!tag.lastModified || !f.lastModified || tag.lastModified === f.lastModified));
}

/* ─────────────────────────────── saving ─────────────────────────────── */

var saveTimer = 0, lastSavedFiles = '';

/**
 * Persist the project. State always; the source files too when the engine
 * accepts Blobs AND they changed — re-writing 147 MB on every slider nudge
 * would be the one way to make an autosave user-visible.
 */
async function save(P, opts) {
    opts = opts || {};
    if (!P || !P.clips.length) return { ok: false, why: 'empty' };

    var state = serialise(P);
    var record = { savedAt: Date.now(), state: state, files: null, music: null, withFiles: false };

    var fileKey = P.clips.map(function (c) { return c.name + ':' + (c.file ? c.file.size : 0); }).join('|')
        + '||' + (P.music && P.music.file ? P.music.name + ':' + P.music.file.size : '');

    var wantFiles = await canStoreBlobs();
    if (wantFiles && (opts.force || fileKey !== lastSavedFiles)) {
        record.files = P.clips.map(function (c) { return c.file; });
        record.music = P.music ? P.music.file : null;
        record.withFiles = true;
    } else if (wantFiles) {
        // Files unchanged — keep the ones already stored rather than rewriting.
        var prev = await get(KEY).catch(function () { return null; });
        if (prev && prev.withFiles && prev.files && prev.files.length === P.clips.length) {
            record.files = prev.files;
            record.music = prev.music;
            record.withFiles = true;
        }
    }

    try {
        await put(KEY, record);
        if (record.withFiles) lastSavedFiles = fileKey;
        return { ok: true, withFiles: record.withFiles };
    } catch (e) {
        // Quota, or an engine that refuses blobs after all. The STATE is the
        // part worth keeping, so drop the bytes and try again — a draft that
        // restores every setting and asks for the files back is far better than
        // no draft.
        if (record.withFiles) {
            record.files = null; record.music = null; record.withFiles = false;
            try {
                await put(KEY, record);
                return { ok: true, withFiles: false, degraded: ((e && e.name) || 'error') };
            } catch (e2) { return { ok: false, why: (e2 && e2.name) || 'error' }; }
        }
        return { ok: false, why: (e && e.name) || 'error' };
    }
}

function scheduleSave(P) {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(function () { save(P).catch(function () {}); }, SAVE_DEBOUNCE_MS);
}

/* ─────────────────────────────── loading ─────────────────────────────── */

async function load() {
    var rec;
    try { rec = await get(KEY); } catch (_) { return null; }
    if (!rec || !rec.state) return null;
    if (Date.now() - (rec.savedAt || 0) > MAX_AGE_MS) { await clear(); return null; }

    var files = null;
    if (rec.withFiles && rec.files && rec.files.length === rec.state.clips.length) {
        files = rec.files.map(function (b, i) {
            if (!b) return null;
            var tag = rec.state.clips[i].file || {};
            // Back as a File, not a Blob: the rest of the tool reads .name and
            // hands the object to the uploader pipeline.
            try {
                return new File([b], tag.name || ('take-' + (i + 1)), {
                    type: b.type || 'video/mp4', lastModified: tag.lastModified || Date.now()
                });
            } catch (_) { return b; }
        });
        if (files.some(function (f) { return !f; })) files = null;
    }

    var music = null;
    if (files && rec.music && rec.state.music) {
        try {
            music = new File([rec.music], rec.state.music.name || 'music',
                { type: rec.music.type || 'audio/mpeg' });
        } catch (_) { music = rec.music; }
    }

    return {
        savedAt: rec.savedAt,
        ageMs: Date.now() - (rec.savedAt || 0),
        state: rec.state,
        files: files,
        music: music,
        withFiles: !!files
    };
}

function clear() {
    lastSavedFiles = '';
    clearTimeout(saveTimer);
    return del(KEY);
}

/* ─────────────────────────────── history ───────────────────────────────
   Snapshots, not commands. Measured: 0.008 ms and ~1 KB per step on an ordinary
   project; a command/inverse scheme would trade that for a class of bugs where
   an inverse drifts from its operation. The cap is bytes because the painted
   case is 200× heavier than the plain one. */

function History(budgetBytes) {
    this.budget = budgetBytes || 6 * 1024 * 1024;
    this.past = [];
    this.future = [];
    this.bytes = 0;
}

History.prototype._trim = function () {
    while (this.past.length > 1 && this.bytes > this.budget) {
        var dropped = this.past.shift();
        this.bytes -= dropped.length;
    }
};

/** Record a state the user could want back. Returns true if it was new. */
History.prototype.push = function (json) {
    if (this.past.length && this.past[this.past.length - 1] === json) return false;
    this.past.push(json);
    this.bytes += json.length;
    this.future.length = 0;
    this._trim();
    return true;
};

History.prototype.canUndo = function () { return this.past.length > 1; };
History.prototype.canRedo = function () { return this.future.length > 0; };

History.prototype.undo = function () {
    if (!this.canUndo()) return null;
    var cur = this.past.pop();
    this.bytes -= cur.length;
    this.future.push(cur);
    return this.past[this.past.length - 1];
};

History.prototype.redo = function () {
    if (!this.canRedo()) return null;
    var next = this.future.pop();
    this.past.push(next);
    this.bytes += next.length;
    this._trim();
    return next;
};

History.prototype.depth = function () { return this.past.length - 1; };

window.CloneDraft = {
    save: save,
    scheduleSave: scheduleSave,
    load: load,
    clear: clear,
    serialise: serialise,
    sameFile: sameFile,
    canStoreBlobs: canStoreBlobs,
    History: History,
    MAX_AGE_MS: MAX_AGE_MS
};
})();
